import { useCallback, useEffect, useMemo } from "react";
import {
    SearchContainer,
    SearchContainerWrapper,
    StyledIntentCategoryDDBlock,
    StyledIntentTopicSearchBlock,
    StyledIntentTopicTableContainer,
    StyledSWReactTableContainer,
    StyledTableWrapper,
    TablePagerContainer,
} from "./styles";
import { SearchInput } from "@similarweb/ui-components";
import { fetchIntentTopicsThunk } from "app/pages/sales-intelligence/sub-modules/intent/store/management/effects/effects";
import { useDispatch, useSelector } from "react-redux";
import {
    selectIntentSearchObject,
    selectIntentTopics,
    selectIntentTopicsTotalCount,
    selectIsTopicFetchError,
    selectIsTopicFetching,
} from "app/pages/sales-intelligence/sub-modules/intent/store/management/selectors/selectors";
import {
    getMappingForSorting,
    getMappingSortingFieldForUi,
    intentTableColumnsConfigGen,
} from "app/pages/sales-intelligence/sub-modules/intent/helpers/columns";
import { setFullSearchObject } from "app/pages/sales-intelligence/sub-modules/intent/store/management/actions/actions";
import { translate } from "app/filters/i18nFilter";
import { SWReactTableOptimized } from "app/components/React/Table/SWReactTableOptimized";
import Pagination from "app/pages/sales-intelligence/sub-modules/contacts-search/components/Pagination/Pagination";
import {
    DOMAINS_COUNT_FIELD_NAME,
    TOPIC_FIELD_NAME,
} from "app/pages/sales-intelligence/sub-modules/intent/types/constants";
import intentTrackingService from "app/pages/sales-intelligence/services/tracking/intent/trackingService";
import IntentCategoryDropDown from "app/pages/sales-intelligence/pages/manage-signals/components/IntentCategoryDropDown/IntentCategoryDropDown";

const IntentTopicTable = () => {
    const dispatch = useDispatch();

    const topics = useSelector(selectIntentTopics);
    const totalCount = useSelector(selectIntentTopicsTotalCount);
    const isTopicLoading = useSelector(selectIsTopicFetching);
    const isTopicFetchError = useSelector(selectIsTopicFetchError);
    const searchObj = useSelector(selectIntentSearchObject);

    const onSearch = useCallback(
        (val: string) => {
            void dispatch(setFullSearchObject({ ...searchObj, searchText: val.trim(), page: 1 }));
        },
        [searchObj],
    );

    useEffect(() => {
        void dispatch(fetchIntentTopicsThunk(searchObj));
    }, [searchObj]);

    useEffect(() => {
        if (searchObj.searchText) {
            void intentTrackingService.trackSearchTermIntentAction(searchObj.searchText, topics);
        }
    }, [topics]);

    const onTrackSortingEvent = (field: string) => {
        switch (field) {
            case TOPIC_FIELD_NAME:
                void intentTrackingService.trackSortTopicAction();
                break;
            case DOMAINS_COUNT_FIELD_NAME:
                void intentTrackingService.trackSortCompaniesAction();
                break;
            default:
                void intentTrackingService.trackSortCategoriesAction();
        }
    };

    const onSort = ({ field, sortDirection }: { field: string; sortDirection: string }) => {
        void dispatch(
            setFullSearchObject({
                ...searchObj,
                orderBy: getMappingForSorting(field),
                asc: sortDirection === "asc",
                page: 1,
            }),
        );

        onTrackSortingEvent(field);
    };

    const onChangePageHandler = useCallback(
        (e: number) => {
            if (e !== searchObj.page) {
                void dispatch(setFullSearchObject({ ...searchObj, page: e }));
            }
        },
        [searchObj],
    );

    const tableColumns = useMemo(() => {
        return intentTableColumnsConfigGen(
            getMappingSortingFieldForUi(searchObj.orderBy),
            searchObj.asc ? "asc" : "desc",
        );
    }, [searchObj.orderBy, searchObj.asc]);

    const tableProps = {
        onSort,
        isLoading: isTopicLoading,
        tableData: { Data: topics, TotalCount: totalCount },
        tableColumns: tableColumns,
        tableHeight: 410,
    };

    return (
        <StyledIntentTopicTableContainer>
            <StyledIntentTopicSearchBlock>
                <SearchContainerWrapper>
                    <SearchContainer>
                        <SearchInput
                            defaultValue={searchObj.searchText}
                            debounce={500}
                            onChange={onSearch}
                            placeholder={translate("si.intent.management.add-topic.search-input.placeholder")}
                        />
                    </SearchContainer>
                </SearchContainerWrapper>

                <StyledIntentCategoryDDBlock>
                    <IntentCategoryDropDown />
                </StyledIntentCategoryDDBlock>
            </StyledIntentTopicSearchBlock>
            <StyledTableWrapper>
                <StyledSWReactTableContainer>
                    <SWReactTableOptimized {...tableProps} />
                </StyledSWReactTableContainer>
                {!isTopicLoading && !isTopicFetchError && totalCount > 0 ? <TablePagerContainer>
                        <Pagination
                            page={searchObj.page}
                            pageSize={100}
                            totalCount={totalCount}
                            onPageChange={onChangePageHandler}
                        />
                    </TablePagerContainer> : null}
            </StyledTableWrapper>
        </StyledIntentTopicTableContainer>
    );
};

export default IntentTopicTable;
